import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Button } from "@babackapp/ui/components/button";
import { ReloadIcon } from "@radix-ui/react-icons";
import ActivityWrapper from "./activity-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import ActivityContentWrapper from "./activity-content-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import useReplayOneStock from "@babackapp/private-api-client/client/use-replay-onestock";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import oneStockLogo from "@/assets/integrations/onestock.svg";

type OneStockActivityProps = {
  activity: ReturnRequestLogDto;
  returnRequestId: string;
};

export default function OneStockActivity({
  activity,
  returnRequestId,
}: OneStockActivityProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const replayOneStock = useReplayOneStock(shop?.id);

  const params = activity.params as {
    action: "create" | "cancel";
    response?: string;
    error?: string;
  };

  const isError = !!params.error;

  let formattedResult: string | undefined = undefined;
  if (params.response !== undefined) {
    try {
      formattedResult = JSON.stringify(
        typeof params.response === "string"
          ? JSON.parse(params.response)
          : params.response,
        null,
        2,
      );
    } catch {
      formattedResult =
        typeof params.response === "string"
          ? params.response
          : String(params.response);
    }
  }

  const handleReplay = async () => {
    try {
      if (replayOneStock.isPending) return;
      await replayOneStock.mutateAsync({
        returnRequestId,
        action: params.action,
      });
    } catch (error) {
      console.error("Error replaying OneStock call:", error);
    }
  };

  return (
    <ActivityWrapper>
      <ActivityIconWrapper
        className={`${isError ? "text-red-500 bg-red-100" : ""}`}
      >
        <img src={oneStockLogo} className="size-4" alt="OneStock logo" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <div className="flex items-center gap-2 flex-1 justify-between w-full min-w-0">
          <Accordion
            type="single"
            collapsible
            className="flex-1 min-w-0 w-full"
          >
            <AccordionItem value="item-1" className="border-0">
              <AccordionTrigger className="p-0 w-full min-w-0">
                <ActivityItemHeader
                  title=""
                  timestamp={activity.date}
                  description={null}
                >
                  <div className="flex flex-col gap-0">
                    <div className="flex items-center gap-2">
                      <span>
                        OneStock:{" "}
                        {t(
                          `activities.oneStock.${params.action}` as Translation,
                        )}
                        {isError && ` - ${t("misc.error")}`}
                      </span>
                    </div>
                  </div>
                </ActivityItemHeader>
              </AccordionTrigger>
              <AccordionContent>
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleReplay}
                  disabled={replayOneStock.isPending}
                  className={`
                    flex items-center gap-1 text-xs mt-2 px-0
                    ${replayOneStock.isPending ? "pointer-events-none opacity-60" : ""}
                  `}
                  tabIndex={0}
                >
                  <ReloadIcon
                    className={`h-4 w-4 ${replayOneStock.isPending ? "animate-spin" : ""}`}
                  />
                  {t("activities.oneStock.replay" as Translation)}
                </Button>
                {params.error && (
                  <div className="text-sm text-red-600 mt-2">
                    <span className="font-medium">{t("misc.error")} : </span>
                    {params.error}
                  </div>
                )}
                {formattedResult && (
                  <pre className="mt-2 rounded-md bg-muted p-4 text-xs overflow-x-auto whitespace-pre-wrap">
                    {formattedResult}
                  </pre>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
